import { ChangeEvent, useEffect, useState } from "react";

import { Button, InputAdornment, TextField, Typography } from "@mui/material";
import classNames from "classnames";
import { GoAlert } from "react-icons/go";
import * as uuid from "uuid";

import { TLesson, TLessonDocument } from "../../../../../@types";
import { lessonType } from "../../../../../constants";
import { validators } from "../../../../../utils";
import { ExplanationPopover } from "../../../../common/ExplanationPopover/ExplanationPopover";
import { Loading } from "../../../../common/Loading/Loading";
import {
	TLocalLessonDocument,
	useMyCourseInstructor,
} from "../../hooks/useMyCourseInstructor";
import { CloudLessonDocument } from "./CloudLessonDocument";
import { LocalLessonDocument } from "./LocalLessonDocument";

export type TLessonItemDetailProps = {
	lesson: TLesson;
};

function minuteToHour(minute: number | null) {
	if (minute === 0) {
		return "0";
	}
	if (minute === null) {
		return "";
	}

	return `${Math.round((minute / 60) * 100) / 100}`;
}

export function LessonItemDetail({ lesson }: TLessonItemDetailProps) {
	const [selectedTab, setSelectedTab] = useState(0);

	const [description, setDescription] = useState(lesson.description ?? "");
	let defaultDescriptionError = "";
	if ((lesson.description ?? "").trim() === "") {
		defaultDescriptionError = "Means to the end goal";
	}
	const [descriptionError, setDescriptionError] = useState(
		defaultDescriptionError,
	);
	const [mediaFiles, setMediaFiles] = useState<
		(TLessonDocument | TLocalLessonDocument)[]
	>(lesson.lessonDocuments);
	const [type, setType] = useState(lesson.type);
	const [teachingTime, setTeachingTime] = useState(
		minuteToHour(lesson.teachingTime),
	);
	const [flightTime, setFlightTime] = useState(minuteToHour(lesson.flightTime));
	const [airplane, setAirplane] = useState(lesson.airplane);
	const [totalCost, setTotalCost] = useState(`${lesson.totalCost ?? ""}`);

	const [loading, setLoading] = useState(false);

	const { updateLesson, updateLessonDocument } = useMyCourseInstructor();

	useEffect(() => {
		setDescription(lesson.description ?? "");
		if ((lesson.description ?? "").trim() === "") {
			setDescriptionError("Means to the end goal");
		}
	}, [lesson.description]);

	useEffect(() => {
		setMediaFiles(lesson.lessonDocuments);
	}, [lesson.lessonDocuments]);

	useEffect(() => {
		setType(lesson.type);
	}, [lesson.type]);

	useEffect(() => {
		setTeachingTime(minuteToHour(lesson.teachingTime));
	}, [lesson.teachingTime]);

	useEffect(() => {
		setFlightTime(minuteToHour(lesson.flightTime));
	}, [lesson.flightTime]);

	useEffect(() => {
		setAirplane(lesson.airplane);
	}, [lesson.airplane]);

	useEffect(() => {
		setTotalCost(`${lesson.totalCost ?? ""}`);
	}, [lesson.totalCost]);

	const onClickChangeTab = (tab: number) => () => {
		setSelectedTab(tab);
	};

	const onSelectFiles = (event: ChangeEvent<HTMLInputElement>) => {
		const files = event.target.files;
		if (!files) {
			return;
		}
		const newMediaFiles = [...mediaFiles];
		for (let i = 0; i < files.length; i++) {
			const file = files.item(i);
			if (
				!file ||
				![
					"image/png",
					"image/jpeg",
					"video/mp4",
					"application/pdf",
					"audio/mpeg",
				].includes(file.type)
			) {
				continue;
			}

			const id = uuid.v4();
			newMediaFiles.push({
				type: "local",
				id,
				file,
			});
		}

		setMediaFiles(newMediaFiles);
	};

	const onClickRemoveFile = (id: string | number) => {
		setMediaFiles((prevState) =>
			prevState.filter((mediaFile) => mediaFile.id !== id),
		);
	};

	// const onClickSelectOnsite = () => {
	// 	setType(lessonType.ONSITE);
	// };

	// const onClickSelectOnline = () => {
	// 	setType(lessonType.ONLINE);
	// 	setTeachingTime("");
	// 	setFlightTime("");
	// 	setAirplane("");
	// };

	const onClickCancel = () => {
		setDescription(lesson.description ?? "");
		setMediaFiles(lesson.lessonDocuments);
		setType(lesson.type);
		setTeachingTime(minuteToHour(lesson.teachingTime));
		setFlightTime(minuteToHour(lesson.flightTime));
		setAirplane(lesson.airplane);
		setTotalCost(`${lesson.totalCost ?? ""}`);
	};

	const onClickSaveLesson = async () => {
		if (isDisabled) {
			return;
		}

		try {
			setLoading(true);
			const promises: Promise<any>[] = [];

			if (
				mediaFiles.map((f) => f.id).join(",") !==
				lesson.lessonDocuments.map((d) => d.id).join(",")
			) {
				const updateLessonDocumentPromise = updateLessonDocument(
					mediaFiles,
					lesson.id,
				);
				promises.push(updateLessonDocumentPromise);
			}

			const body: Partial<TLesson> = {};
			if (description.trim() !== lesson.description.trim()) {
				body.description = description.trim();
			}
			if (type !== lesson.type) {
				body.type = type;
			}
			if (Number(teachingTime) !== Number(minuteToHour(lesson.teachingTime))) {
				if (teachingTime === "") {
					body.teachingTime = null;
				} else {
					body.teachingTime = Number(teachingTime) * 60;
				}
			}
			if (Number(flightTime) !== Number(minuteToHour(lesson.flightTime))) {
				if (flightTime === "") {
					body.flightTime = null;
				} else {
					body.flightTime = Number(flightTime) * 60;
				}
			}
			if (airplane !== lesson.airplane) {
				body.airplane = airplane;
			}
			if (Number(totalCost) !== Number(`${lesson.totalCost ?? ""}`)) {
				body.totalCost = Number(totalCost);
			}

			if (Object.keys(body).length > 0) {
				const updateLessonPromise = updateLesson(body, lesson.id);
				promises.push(updateLessonPromise);
			}

			await Promise.all(promises);
		} finally {
			setLoading(false);
		}
	};

	const isEdited =
		description.trim() !== lesson.description.trim() ||
		mediaFiles.map((f) => f.id).join(",") !==
			lesson.lessonDocuments.map((d) => d.id).join(",") ||
		type !== lesson.type ||
		Number(teachingTime) !== Number(minuteToHour(lesson.teachingTime)) ||
		Number(flightTime) !== Number(minuteToHour(lesson.flightTime)) ||
		airplane !== lesson.airplane ||
		Number(totalCost) !== Number(`${lesson.totalCost ?? ""}`);

	const isDisabled =
		!isEdited ||
		description.trim() === "" ||
		Number(totalCost) === 0 ||
		Number(teachingTime) + Number(flightTime) === 0;

	const onsiteWarning =
		type === lessonType.ONSITE &&
		Number(teachingTime) + Number(flightTime) === 0;
	const scheduleWarning =
		lesson.type === lessonType.ONSITE &&
		Number(minuteToHour(lesson.teachingTime)) +
			Number(minuteToHour(lesson.flightTime)) ===
			0;

	return (
		<>
			<div className="lesson-detail-container">
				<div className="common-input">
					<TextField
						size="small"
						autoComplete="off"
						id="lesson-description"
						onChange={(e) => {
							setDescription(e.target.value);
							if (e.target.value.trim() === "") {
								setDescriptionError("Means to the end goal");
							} else {
								setDescriptionError("");
							}
						}}
						value={description}
						fullWidth
						multiline
						className={classNames("", { invalid: !!descriptionError })}
						minRows={2}
						placeholder="What knowledge, skills, behaviors, and competencies are you transferring?"
						slotProps={{
							input: {
								className: "normal-text",
								classes: {
									input: "input-placeholder",
								},
							},
						}}
					/>
					{descriptionError && (
						<Typography className="small-text invalid-message">
							{descriptionError}
						</Typography>
					)}
				</div>
				<div className="lesson-tabs-container item-separator-big">
					<div
						className={classNames("tab-item", {
							selected: selectedTab === 0,
						})}
						style={{ display: "flex", alignContent: "center", gap: "8px" }}
						onClick={onClickChangeTab(0)}>
						<Typography className="big-text bold">Schedule</Typography>
						{scheduleWarning && (
							<div>
								<GoAlert className="normal-icon warning-icon" />
							</div>
						)}
					</div>
					<div
						className={classNames("tab-item", {
							selected: selectedTab === 1,
						})}
						style={{ display: "flex", alignContent: "center", gap: "8px" }}
						onClick={onClickChangeTab(1)}>
						<Typography className="big-text bold">Learning Material</Typography>
					</div>
				</div>
				{selectedTab === 0 && (
					<>
						{/* <div
							className="lesson-type-item item-separator-small"
							onClick={onClickSelectOnsite}>
							<Checkbox
								checked={type === lessonType.ONSITE}
								onChange={onClickSelectOnsite}
								icon={<IoMdRadioButtonOff className="normal-icon" />}
								checkedIcon={
									<IoMdRadioButtonOn className="normal-icon" color="#3A7BFF" />
								}
							/>
							<Typography className="normal-text">
								Yes, this class requires scheduling.
							</Typography>
						</div> */}
						{type === lessonType.ONSITE && (
							<>
								<div className="learning-time-container item-separator-small">
									<div className="learning-time-item">
										<div
											style={{
												display: "flex",
												alignItems: "center",
												gap: "8px",
											}}>
											<Typography className="normal-text bold">
												Teaching Time
											</Typography>
											<ExplanationPopover text="Time you invest on the ground to build knowledge and review progress." />
										</div>
										<div className="common-input item-separator-tiny">
											<TextField
												size="small"
												autoComplete="off"
												id="lesson-teaching-time"
												onChange={(e) => {
													if (
														!validators.time.test(e.target.value) &&
														e.target.value !== ""
													) {
														return;
													}

													setTeachingTime(e.target.value);
												}}
												value={teachingTime}
												fullWidth
												slotProps={{
													input: {
														className: "normal-text",
														classes: {
															input: "input-placeholder",
														},
														inputMode: "numeric",
														endAdornment: (
															<InputAdornment position="end">
																<Typography className="normal-text">
																	hrs
																</Typography>
															</InputAdornment>
														),
													},
												}}
											/>
										</div>
									</div>
									<div className="learning-time-item">
										<div
											style={{
												display: "flex",
												alignItems: "center",
												gap: "8px",
											}}>
											<Typography className="normal-text bold">
												Flight Time
											</Typography>
											<ExplanationPopover text="Time you invest in the air or in the simulator to develop skills." />
										</div>
										<div className="common-input item-separator-tiny">
											<TextField
												size="small"
												autoComplete="off"
												id="lesson-flight-time"
												onChange={(e) => {
													if (
														!validators.time.test(e.target.value) &&
														e.target.value !== ""
													) {
														return;
													}

													setFlightTime(e.target.value);
												}}
												value={flightTime}
												fullWidth
												slotProps={{
													input: {
														className: "normal-text",
														classes: {
															input: "input-placeholder",
														},
														inputMode: "numeric",
														endAdornment: (
															<InputAdornment position="end">
																<Typography className="normal-text">
																	hrs
																</Typography>
															</InputAdornment>
														),
													},
												}}
											/>
										</div>
									</div>
									<div className="learning-time-item">
										<div
											style={{
												display: "flex",
												alignItems: "center",
												gap: "8px",
											}}>
											<Typography className="normal-text bold">
												Aircraft or Sim Type
											</Typography>
											<ExplanationPopover text="Enter Aircraft Make and Model or Sim Type if you Own or Control the equipment. Common Sim Types are Desktop, Custom-built Cockpit,  Cockpit Procedures Trainer, and Certified Flight Training Device. " />
										</div>
										<div className="common-input item-separator-tiny">
											<TextField
												size="small"
												autoComplete="off"
												id="lesson-airplane"
												onChange={(e) => {
													setAirplane(e.target.value);
												}}
												value={airplane}
												placeholder="e.g. Piper Cub"
												fullWidth
												slotProps={{
													input: {
														className: "normal-text",
														classes: {
															input: "input-placeholder",
														},
													},
												}}
											/>
										</div>
									</div>
								</div>
								<Typography className="item-separator-small">
									<span className="normal-text bold">Block Time:</span>
									<span
										style={{ marginLeft: "16px", marginRight: "16px" }}
										className="title-text bold">
										{(Number(teachingTime) + Number(flightTime)).toFixed(1)}
									</span>
									<span className="normal-text bold">hrs</span>
								</Typography>
								{onsiteWarning && (
									<Typography className="small-text" color="#3A7BFF">
										Block Time needs to be greater than 0
									</Typography>
								)}
							</>
						)}
						{/* <div className="lesson-type-item" onClick={onClickSelectOnline}>
							<Checkbox
								checked={type === lessonType.ONLINE}
								onChange={onClickSelectOnline}
								icon={<IoMdRadioButtonOff className="normal-icon" />}
								checkedIcon={
									<IoMdRadioButtonOn className="normal-icon" color="#3A7BFF" />
								}
							/>
							<Typography className="normal-text">
								No, direct interactions are limited to online messages.
							</Typography>
						</div> */}
					</>
				)}
				{selectedTab === 1 && (
					<>
						{mediaFiles.length > 0 && (
							<div className="lesson-media-container item-separator-small">
								{mediaFiles.map((mediaFile) => {
									if (mediaFile.type === "local") {
										return (
											<LocalLessonDocument
												lessonDocument={mediaFile}
												onClickRemoveFile={onClickRemoveFile}
												key={mediaFile.id}
											/>
										);
									}

									return (
										<CloudLessonDocument
											lessonDocument={mediaFile}
											onClickRemoveFile={onClickRemoveFile}
											key={mediaFile.id}
										/>
									);
								})}
							</div>
						)}
						<div className="file-input-container item-separator-small">
							<input
								type="file"
								value=""
								accept="image/png,image/jpeg,video/mp4,application/pdf,audio/mpeg"
								className="file-input"
								onChange={onSelectFiles}
								multiple
							/>
							<div className="file-input-content-container">
								<Typography className="normal-text" textAlign="center">
									Drag 'n' drop or click to share content with your students. We
									support MP4, PDF, PNG, JPG/JPEG.
								</Typography>
							</div>
						</div>
					</>
				)}
				<div className="lesson-price-container item-separator-big">
					<Typography className="big-text bold">Total Cost</Typography>
					<TextField
						size="small"
						autoComplete="off"
						id="lesson-price"
						onChange={(e) => {
							if (
								!validators.price.test(e.target.value) &&
								e.target.value !== ""
							) {
								return;
							}

							setTotalCost(e.target.value);
						}}
						value={totalCost}
						slotProps={{
							input: {
								className: "normal-text",
								classes: {
									input: "input-placeholder",
								},
								inputMode: "numeric",
								startAdornment: (
									<InputAdornment position="start">
										<Typography className="normal-text">$</Typography>
									</InputAdornment>
								),
								endAdornment: (
									<InputAdornment position="end">
										<Typography className="normal-text">USD</Typography>
									</InputAdornment>
								),
							},
						}}
					/>
				</div>
				<div className="separator-big" />
				<div className="button-container item-separator-small">
					<Button
						className="normal-button disabled-button"
						disabled={!isEdited}
						onClick={onClickCancel}>
						Cancel
					</Button>
					<Button
						className={classNames("normal-button", {
							"primary-button": !isDisabled,
							"disabled-button": isDisabled,
						})}
						disabled={isDisabled}
						onClick={onClickSaveLesson}>
						Save
					</Button>
				</div>
			</div>
			{loading && <Loading absolute />}
		</>
	);
}
